// R9-5 — THE CHART REPORT, AND THE ONE HELPER THAT DECIDES WHETHER A BOX GETS DRAWN AT ALL.
//
// Two units, moved together because one calls the other and neither was reachable by a gate:
//
//   • `reportSectionCard` — T9-2/T9-3's law, and the most-called law in the report layer. Every
//     per-card and per-section box on this site goes through it, and the whole point of it is that
//     it returns NOTHING when there is nothing to put inside. `scripts/report-sections.mjs` held
//     that by MATCHING SOURCE TEXT in App.tsx: that `function reportSectionCard(` appeared and that
//     the string `if (!paras.length) return null;` appeared somewhere in the same 16,000-line file.
//     Those two facts can both be true of a function that then draws the box anyway. Out here the
//     gate CALLS it and reads what comes back.
//   • `ReportView` — the birth chart report, the sibling of the narrative shell R9-1 moved, and the
//     second of the three identity blocks the W12-2 fixture counts.
//
// Moved VERBATIM, with the three types only these render. App.tsx imports `reportSectionCard` back
// (seven other call sites) and `ReportData` back (the chart flows are typed by it).

import type { ReactNode } from "react";
import { titleCase } from "../lib/format";
import { SIGN_GLYPHS, PLANET_GLYPHS, ROMAN_HOUSES } from "../catalogs/glyphs";
import menuLogoUrl from "../assets/menu-logo.webp";

export type ReportPlanet = {
  name: string;
  sign: string;
  // CE-6 — A TIMELESS CHART'S PLACEMENTS CARRY NEITHER OF THESE, AND THE TYPE SAYS SO. They were
  // required, so the timeless mapper could not have produced a `ReportPlanet` at all without
  // inventing a house and a degree — which is the trap this ruling is about, caught by the type
  // rather than by a reviewer. Optional here and ABSENT on the object there (`timelessViolations`
  // holds that at runtime, because an optional field is not a promise that nobody set it).
  house?: number;
  degreeText?: string;
  retrograde?: boolean;
  // Her Moon clause: "your Moon is in X or Y depending on your hour", never one picked for her.
  alternateSign?: string | null;
  title: string;
  interpretation: string;
};

export type ReportHouse = {
  house: number;
  sign: string;
  degreeText: string;
  title: string;
  interpretation: string;
};

export type ReportData = {
  header: { name: string; dateText: string; timeText: string; place: string };
  chartUrl: string | null;
  zodiac: string;
  // CE-6: null on a timeless chart. Naming Placidus over an empty house table would be the wheel's
  // lie in text, which is the one thing her ruling forbids most plainly.
  houseSystem: string | null;
  planets: ReportPlanet[];
  houses: ReportHouse[];
  overview: string;
  interpreted: boolean;
  // CE-6: this reading was cast with no birth time. The flag decides what the page DRAWS; the note
  // is the one warm sentence the server composed with it.
  timeless?: boolean;
  timelessNote?: string;
};

// T9-2 / T9-3 — A SECTION EXISTS ONLY IF IT CARRIES CONTENT (owner ruling, 2026-07-29).
//
// WHAT SHE OPENED: a general reading whose per-card sections carried the card's name and NOTHING
// ELSE — three named boxes, all empty-bodied, sitting above a full evaluation. Every one of these
// blocks drew its box and its title first and asked whether there was anything to put inside second,
// so the moment a body was missing the member got a labelled void with her reading underneath it.
// An empty box is not a reading; it is a build failure wearing one.
//
// So one component decides it for every per-card and per-section box in the app: NO BODY, NO BOX. A
// reading type that legitimately has no per-section breakdown renders none, rather than a row of
// empty shells. `scripts/report-sections.mjs` asserts this in the fast gate, because the rule is only
// worth anything if the next section block cannot quietly go back to drawing itself first.
// It is a FUNCTION that returns the box, not a component, for one dull reason worth writing down:
// this project has no @types/react, so `key` on a component's props is a type error every time,
// while `key` on a plain <div> is not. Seven call sites, seven errors, on a gate that exists to stop
// exactly that kind of drift. The behaviour is identical either way.
export function reportSectionCard(key: string | number, title: ReactNode, body?: string | null,
  opts?: { wide?: boolean; heading?: boolean }) {
  const paras = String(body || "").split("\n").map((line) => line.trim()).filter(Boolean);
  if (!paras.length) return null;
  return (
    <div key={key} className={`chart-report__card${opts?.wide === false ? "" : " chart-report__card--wide"}`}>
      {opts?.heading ? <h4>{title}</h4> : <p className="chart-report__card-title">{title}</p>}
      {paras.map((para, index) => <p key={index}>{para}</p>)}
    </div>
  );
}

// CE-6 — HOW A PLACEMENT IS SAID, ON THIS SIDE OF THE BOUNDARY. It is the same sentence the server
// composes from `src/timelessChart.js`, and it is re-derived here rather than imported for the one
// reason this component exists out here at all: the rule for every module a gate reaches is that it
// imports nothing but types. **THE LAW IS ONE LINE WIDE AND THAT IS EXACTLY WHY IT NEEDS HOLDING** —
// `scripts/ce6-timeless-chart.mjs` lifts this function's body out of this file and RUNS it against
// `timelessSignText` over the same placements, so the two spellings cannot drift.
function signText(planet: ReportPlanet): string {
  return planet.alternateSign ? `${planet.sign} or ${planet.alternateSign}` : planet.sign;
}

export function ReportView({ data }: { data: ReportData }) {
  const overviewParas = (data.overview || "").split("\n").map((line) => line.trim()).filter(Boolean);
  // CE-6: `=== true` and never truthily. This arrives off a jsonb column through JSON, and
  // `Boolean("false")` is `true` (R136/R137) — on a flag that decides whether a member is shown
  // houses that do not exist.
  const timeless = data.timeless === true;
  // A house table with no rows would draw an empty box with a heading over it, which is T9-2's
  // no-body-no-box law inverted. The whole section is asked, once, here.
  const hasHouses = Array.isArray(data.houses) && data.houses.length > 0;

  return (
    <article className="chart-report">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">Birth Chart Report</p>
      </header>

      <section className="chart-report__info">
        <h3>General Information</h3>
        <div className="chart-report__info-grid">
          <p><span>Name</span>{data.header.name}</p>
          <p><span>Place of Birth</span>{data.header.place}</p>
          <p><span>Date of Birth</span>{data.header.dateText}</p>
          <p><span>Time of Birth</span>{data.header.timeText || "Not recorded"}</p>
        </div>
      </section>

      {/* CE-6 — HER ONE WARM LINE, AND IT SITS ABOVE THE READING RATHER THAN UNDER IT. A member who
          scrolls a full page of placements and only then learns why there are no houses has already
          spent the whole reading wondering what is missing. It is a NOTE, not a warning: this is her
          birth chart, and the sentence names the one thing it cannot hold and how to complete it. */}
      {timeless && data.timelessNote ? (
        <p className="chart-report__timeless">{data.timelessNote}</p>
      ) : null}

      {data.chartUrl ? (
        <section className="chart-report__wheel">
          {/* LF-2: every report SAVED BEFORE the wheel was kept as bytes carries a presigned vendor
              link that has since expired, and a dead link draws a BROKEN IMAGE — which is what she
              reported. Those rows cannot be repaired without buying the chart again, so the honest
              degradation is no wheel rather than a hole in her reading. No hook: this component is
              CALLED by its gate (R9-5) and a `useState` here would put it out of reach. */}
          <img src={data.chartUrl} alt={`Natal chart wheel for ${data.header.name}`}
            onError={(e) => { const box = e.currentTarget.closest("section"); if (box) box.hidden = true; }} /></section>
      ) : null}

      <section className="chart-report__tables">
        <div className="chart-report__table">
          <div className="chart-report__table-head">Zodiac: {data.zodiac}</div>
          <table>
            <tbody>
              {data.planets.map((planet) => (
                <tr key={planet.name}>
                  <td className="glyph">{PLANET_GLYPHS[planet.name] || "•"}</td>
                  <td className="name">{planet.name}{planet.retrograde ? " ℞" : ""}</td>
                  {/* A straddling body has no single glyph to draw, and drawing the first sign's
                      would pick one on her behalf in the most literal way available. */}
                  <td className="glyph">{planet.alternateSign ? "" : (SIGN_GLYPHS[planet.sign] || "")}</td>
                  <td className="name">{signText(planet)}</td>
                  {/* CE-6: no degree column on a timeless chart. A degree is a position at an
                      INSTANT (the Moon moves ~13° a day), so printing one read off the midnight cast
                      is the house number's trap wearing a decimal point. */}
                  {timeless ? null : <td className="deg">{planet.degreeText}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* CE-6 — THE HOUSE TABLE IS NOT DRAWN AT ALL. Not empty, not "unavailable": absent. A
            twelve-row table with nothing in it is a shape a member reads as a broken reading, and a
            table drawn from an empty list with `Placidus Houses` over it would be a heading making
            a claim its own body cannot support. */}
        {hasHouses ? (
          <div className="chart-report__table">
            <div className="chart-report__table-head">{titleCase(data.houseSystem || "")} Houses</div>
            <table>
              <tbody>
                {data.houses.map((house) => (
                  <tr key={house.house}>
                    <td className="rn">
                      {ROMAN_HOUSES[house.house - 1]}
                      {house.house === 1 ? " ASC" : house.house === 10 ? " MC" : ""}
                    </td>
                    <td className="glyph">{SIGN_GLYPHS[house.sign] || ""}</td>
                    <td className="name">{house.sign}</td>
                    <td className="deg">{house.degreeText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="chart-report__reading">
        <h3>Your Chart, Interpreted</h3>
        {overviewParas.map((para, index) => (
          <p key={index} className="chart-report__overview">{para}</p>
        ))}

        <h4>Planets &amp; Points</h4>
        <div className="chart-report__cards">
          {data.planets.map((planet) => reportSectionCard(
            planet.name,
            <><span className="glyph">{PLANET_GLYPHS[planet.name] || "•"}</span> {planet.title}</>,
            planet.interpretation, { wide: false },
          ))}
        </div>

        {/* CE-6: same law one layer down. The heading is inside the condition, not above it — a
            "Houses" heading over nothing is exactly the labelled void T9-2 exists to refuse, and
            `reportSectionCard` alone cannot prevent it because it never sees the heading. */}
        {hasHouses ? (
          <>
            <h4>Houses</h4>
            <div className="chart-report__cards">
              {data.houses.map((house) => reportSectionCard(
                house.house,
                <><span className="glyph">{SIGN_GLYPHS[house.sign] || ""}</span> {house.title}</>,
                house.interpretation, { wide: false },
              ))}
            </div>
          </>
        ) : null}

        {!data.interpreted ? (
          <p className="chart-report__note">
            Written interpretations are temporarily unavailable, so grounded summaries are shown. Every placement above is still calculated from your exact birth data.
          </p>
        ) : null}
      </section>
    </article>
  );
}
